// Meaning search for the open team: whether it's on, and keeping its vectors current as content changes
import { useCallback, useEffect, useRef, useState } from "react"
import { app } from "@/lib/app"
import { atLeast, type CmsAccess, type CmsSection } from "../permissions"
import type { KBStore } from "../../articles/types"
import type { ServiceStore } from "../../service-map/types"
import type { SiteConfigStore } from "../../site-config/types"
import {
  articleRecordSections, articleSections, blockRecordSections, providerRecordSections, sectionKey, serviceRecordSections,
  serviceSections, siteSections, type IndexSection, type SearchArea,
} from "./sections"
import { applySync, clearIndex, hasIndex, loadIndex, loadRecordIndex, planSync, type SyncProgress } from "./sync"

// Waits for a pause in editing before re-embedding
const AUTO_SYNC_DELAY_MS = 8000

export type SearchIndexState = "checking" | "off" | "on" | "unavailable"

// One record's place in the index: all its text embedded, some waiting, or being embedded now
export interface RecordIndexStatus {
  state: "indexed" | "pending" | "indexing"
  // Languages whose text is embedded
  indexedLocales: string[]
  pendingLocales: string[]
}

export type IndexedKind = IndexSection["kind"]

// One section of a record as the editor's index view shows it
export interface IndexedSectionView {
  locale: string
  chunk: number
  content: string
  // removing: stored, but the record no longer has this text
  state: "indexed" | "pending" | "removing"
  embeddedAt?: string
}

interface Section<T> { store: T; loaded: boolean }

export interface SearchIndexDeps {
  teamId: string
  cms: Section<KBStore>
  svc: Section<ServiceStore>
  siteconfig: Section<SiteConfigStore>
  perms: Record<CmsSection, CmsAccess>
}

const AREA_SECTION: Record<SearchArea, CmsSection> = { articles: "articles", services: "serviceMap", site: "siteConfig" }

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err))

// The table or function isn't there until the migration runs
const isMissingTable = (message: string) => /does not exist|could not find the table|schema cache/i.test(message)

export function useSearchIndex({ teamId, cms, svc, siteconfig, perms }: SearchIndexDeps) {
  const [state, setState] = useState<SearchIndexState>("checking")
  const [progress, setProgress] = useState<SyncProgress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null)

  // Latest inputs for the sync to read when it runs
  const depsRef = useRef({ cms, svc, siteconfig, perms, teamId })
  depsRef.current = { cms, svc, siteconfig, perms, teamId }
  const runningRef = useRef<Promise<void> | null>(null)
  const againRef = useRef(false)
  const abortRef = useRef<AbortController | null>(null)
  // What's stored, section key -> hash, for the areas this user syncs; a count re-renders on change
  const storedRef = useRef(new Map<string, string>())
  const storedAreasRef = useRef(new Set<SearchArea>())
  const [storedCount, setStoredCount] = useState(0)
  const [syncing, setSyncing] = useState(false)

  const setStored = (areas: SearchArea[], entries: [string, string][]) => {
    const next = new Map([...storedRef.current].filter(([k]) => !areas.includes(k.split("|")[0] as SearchArea)))
    for (const [k, h] of entries) next.set(k, h)
    storedRef.current = next
    for (const a of areas) storedAreasRef.current.add(a)
    setStoredCount(next.size)
  }

  // Areas this user can edit whose content has loaded; only these are synced
  const syncableAreas = useCallback((): SearchArea[] => {
    const d = depsRef.current
    const loaded: Record<SearchArea, boolean> = { articles: d.cms.loaded, services: d.svc.loaded, site: d.siteconfig.loaded }
    return (Object.keys(AREA_SECTION) as SearchArea[])
      .filter(a => loaded[a] && atLeast(d.perms[AREA_SECTION[a]], "edit"))
  }, [])

  // Whether the team already has vectors, on load and on team switch
  useEffect(() => {
    let cancelled = false
    abortRef.current?.abort()
    setState("checking")
    setError(null)
    setProgress(null)
    storedRef.current = new Map()
    storedAreasRef.current = new Set()
    setStoredCount(0)
    if (!teamId) return
    hasIndex(teamId).then(
      on => { if (!cancelled) setState(on ? "on" : "off") },
      err => {
        if (cancelled) return
        const message = errText(err)
        setState("unavailable")
        setError(isMissingTable(message) ? "Meaning search isn't set up in the database yet (run scms-search.sql)." : message)
      },
    )
    return () => { cancelled = true }
  }, [teamId])

  const runSync = useCallback(async (): Promise<void> => {
    // One sync at a time; a change during a run queues one more
    if (runningRef.current) { againRef.current = true; return runningRef.current }
    const run = (async () => {
      const { teamId: team, cms, svc, siteconfig } = depsRef.current
      const areas = syncableAreas()
      if (!team || !areas.length) return
      const controller = new AbortController()
      abortRef.current = controller
      setSyncing(true)
      try {
        const keys = await app.fetchAPIkeys(team)
        const apiKey = (keys as Record<string, string | undefined>)?.openai
        if (!apiKey) throw new Error("Add an OpenAI API key in Settings to use meaning search.")

        const sections: IndexSection[] = []
        if (areas.includes("articles")) sections.push(...articleSections(cms.store))
        if (areas.includes("services")) sections.push(...serviceSections(svc.store))
        if (areas.includes("site")) sections.push(...siteSections(siteconfig.store))

        const existing = await loadIndex(team, areas)
        setStored(areas, existing.map(r => [
          sectionKey({ area: r.area, kind: r.kind, recordId: r.record_id, locale: r.locale, chunk: r.chunk }), r.content_hash,
        ]))
        const plan = planSync(sections, existing)
        if (plan.embed.length || plan.remove.length) {
          await applySync(team, plan, apiKey, p => setProgress(p.total ? p : null), controller.signal)
        }
        if (!controller.signal.aborted) {
          setStored(areas, sections.map(s => [sectionKey(s), s.hash]))
          setError(null)
          setLastSyncedAt(Date.now())
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(errText(err))
      } finally {
        setProgress(null)
        setSyncing(false)
      }
    })()
    runningRef.current = run
    try { await run } finally {
      runningRef.current = null
      if (againRef.current) { againRef.current = false; void runSync() }
    }
  }, [syncableAreas])

  // Keeps the vectors current while it's on
  useEffect(() => {
    if (state !== "on") return
    const t = setTimeout(() => { void runSync() }, lastSyncedAt ? AUTO_SYNC_DELAY_MS : 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, cms.store, svc.store, siteconfig.store, cms.loaded, svc.loaded, siteconfig.loaded, runSync])

  // The auto-sync effect runs the first full index
  const turnOn = useCallback(() => {
    setLastSyncedAt(null)
    setState("on")
  }, [])

  const turnOff = useCallback(async () => {
    abortRef.current?.abort()
    const areas = (Object.keys(AREA_SECTION) as SearchArea[])
      .filter(a => atLeast(depsRef.current.perms[AREA_SECTION[a]], "edit"))
    try {
      await clearIndex(depsRef.current.teamId, areas)
      setStored(areas, [])
      setState("off")
      setError(null)
      setLastSyncedAt(null)
    } catch (err) {
      setError(errText(err))
    }
  }, [])

  // Null when search is off, the area isn't synced by this user, or the record has no text to index
  // The record's text as it would be indexed now
  const currentSections = (kind: IndexedKind, id: string): IndexSection[] => {
    const d = depsRef.current
    if (kind === "article") { const a = d.cms.store.articles[id]; return a ? articleRecordSections(a) : [] }
    if (kind === "provider") { const p = d.svc.store.providers[id]; return p ? providerRecordSections(p) : [] }
    if (kind === "service") { const sv = d.svc.store.services[id]; return sv ? serviceRecordSections(sv, d.svc.store) : [] }
    const b = d.siteconfig.store.pageContents[id]
    return b ? blockRecordSections(b, d.siteconfig.store.defaultLocale) : []
  }

  const recordStatus = useCallback((kind: IndexedKind, id: string): RecordIndexStatus | null => {
    if (state !== "on") return null
    const sections = currentSections(kind, id)
    if (!sections.length || !storedAreasRef.current.has(sections[0].area)) return null

    const pending = new Set<string>()
    const all = new Set<string>()
    for (const s of sections) {
      all.add(s.locale)
      if (storedRef.current.get(sectionKey(s)) !== s.hash) pending.add(s.locale)
    }
    return {
      state: !pending.size ? "indexed" : syncing ? "indexing" : "pending",
      indexedLocales: [...all].filter(l => !pending.has(l)),
      pendingLocales: [...pending],
    }
    // storedCount re-creates this when the stored index changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, syncing, storedCount, cms.store, svc.store, siteconfig.store])

  // Each section of the record, compared with what's stored, for the editor's index view
  const recordDetail = useCallback(async (kind: IndexedKind, id: string): Promise<IndexedSectionView[]> => {
    const sections = currentSections(kind, id)
    if (!sections.length) return []
    const stored = await loadRecordIndex(depsRef.current.teamId, sections[0].area, kind, id)
    const byKey = new Map(stored.map(r => [`${r.locale}|${r.chunk}`, r]))
    const views: IndexedSectionView[] = sections.map(sec => {
      const row = byKey.get(`${sec.locale}|${sec.chunk}`)
      byKey.delete(`${sec.locale}|${sec.chunk}`)
      return {
        locale: sec.locale, chunk: sec.chunk, content: sec.content,
        state: row?.content_hash === sec.hash ? "indexed" : "pending",
        embeddedAt: row?.content_hash === sec.hash ? row.updated_at : undefined,
      }
    })
    for (const row of byKey.values()) {
      views.push({ locale: row.locale, chunk: row.chunk, content: row.content, state: "removing", embeddedAt: row.updated_at })
    }
    return views.sort((a, b) => a.locale.localeCompare(b.locale) || a.chunk - b.chunk)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const canManage = (Object.keys(AREA_SECTION) as SearchArea[]).some(a => atLeast(perms[AREA_SECTION[a]], "edit"))

  return { state, progress, error, lastSyncedAt, canManage, storedCount, recordStatus, recordDetail, turnOn, turnOff, syncNow: runSync }
}

export type SearchIndex = ReturnType<typeof useSearchIndex>
